'use client';

import { useState, useEffect } from 'react';
import { Paper, Text, Group, Button, Stack, Anchor } from '@mantine/core';
import { IconCookie } from '@tabler/icons-react';

/**
 * Cookie Consent Banner for GDPR Compliance
 * 
 * Manages user consent for non-essential cookies (Google Analytics).
 * Essential cookies (authentication) don't require consent.
 */
export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    // Check if user has already made a choice
    // Ensure localStorage is available (may be null in some environments)
    if (typeof window !== 'undefined' && window.localStorage) {
      const consent = localStorage.getItem('cookie-consent');
      if (!consent) {
        setShowBanner(true);
      }
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
    // Reload to load analytics
    window.location.reload();
  };

  const handleReject = () => {
    localStorage.setItem('cookie-consent', 'rejected');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
  };

  // Don't render on server or if banner shouldn't show
  if (!isClient || !showBanner) {
    return null;
  }

  return (
    <Paper
      shadow="lg"
      p="md"
      withBorder
      style={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        right: 20,
        maxWidth: 600,
        margin: '0 auto',
        zIndex: 9999,
        backgroundColor: 'white',
      }}
    >
      <Stack gap="md">
        <Group gap="sm" align="flex-start">
          <IconCookie size={24} style={{ marginTop: 4, flexShrink: 0 }} />
          <Stack gap="xs" style={{ flex: 1 }}>
            <Text fw={600} size="sm">
              Cookie Preferences
            </Text>
            <Text size="sm" c="dimmed">
              We use essential cookies for authentication and platform functionality. 
              We&apos;d also like to use analytics cookies (Google Analytics) to understand how 
              you use our site and improve your experience.
            </Text>
            <Text size="xs" c="dimmed">
              Learn more in our{' '}
              <Anchor href="/privacy" size="xs" target="_blank">
                Privacy Policy
              </Anchor>
              .
            </Text>
          </Stack>
        </Group>

        <Group gap="sm" justify="flex-end">
          <Button variant="subtle" color="gray" onClick={handleReject} size="sm">
            Reject Non-Essential
          </Button>
          <Button onClick={handleAccept} size="sm">
            Accept All
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

/**
 * Check if user has consented to analytics cookies
 * Used by GoogleAnalytics component
 */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  const consent = localStorage.getItem('cookie-consent');
  return consent === 'accepted';
}

/**
 * Reset cookie consent (for testing or user preference changes)
 */
export function resetCookieConsent(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('cookie-consent');
  localStorage.removeItem('cookie-consent-date');
  window.location.reload();
}
